import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ChevronLeft, Mail, Lock, User, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setSession } = useAuth();
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ name: '', email: '', password: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Signup-OTP flow state
  const [signupStep, setSignupStep] = useState('form'); // 'form' | 'otp'
  const [otpCode, setOtpCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const from = location.state?.from || '/';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate(from, { replace: true });
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    }
    setForgotLoading(false);
  };

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => setResendCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!signupData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/signup/send-otp`, {
        name: signupData.name.trim(),
        email: signupData.email.trim().toLowerCase(),
        password: signupData.password,
      });
      if (res.data?.success) {
        toast.success('OTP sent to your email');
        setSignupStep('otp');
        setOtpCode('');
        setResendCooldown(30);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otpCode)) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/signup/verify-otp`, {
        email: signupData.email.trim().toLowerCase(),
        otp: otpCode.trim(),
      });
      if (res.data?.success) {
        setSession(res.data.session_token, res.data.user);
        toast.success('Account created successfully!');
        navigate(from, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/signup/send-otp`, {
        name: signupData.name.trim(),
        email: signupData.email.trim().toLowerCase(),
        password: signupData.password,
      });
      if (res.data?.success) {
        toast.success('A new OTP has been sent');
        setOtpCode('');
        setResendCooldown(30);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend OTP');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to home
        </Button>

        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#6da036] flex items-center justify-center shadow-lg mx-auto mb-4">
            <User className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to GeoStays</h1>
          <p className="text-gray-600">Sign in to your account or create a new one</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login">
                {showForgotPassword ? (
                  // Forgot Password View
                  <>
                    <button
                      onClick={() => { setShowForgotPassword(false); setForgotSent(false); }}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700 mt-4 mb-2"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Back to login
                    </button>
                    <CardTitle>Reset your password</CardTitle>
                    <CardDescription className="mt-2">
                      {forgotSent
                        ? "We've sent a password reset link to your email. Please check your inbox."
                        : "Enter your email and we'll send you a link to reset your password."}
                    </CardDescription>

                    {!forgotSent ? (
                      <form onSubmit={handleForgotPassword} className="space-y-4 mt-6">
                        <div>
                          <Label htmlFor="forgot-email">Email</Label>
                          <div className="relative mt-2">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="forgot-email"
                              type="email"
                              placeholder="your.email@example.com"
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                              className="pl-10"
                              required
                              data-testid="forgot-email-input"
                            />
                          </div>
                        </div>
                        <Button
                          type="submit"
                          disabled={forgotLoading}
                          className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                          data-testid="forgot-submit-btn"
                        >
                          {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                      </form>
                    ) : (
                      <div className="mt-6 space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                          <Mail className="h-8 w-8 text-green-600 mx-auto mb-2" />
                          <p className="text-sm text-green-800">Check your email for the reset link</p>
                        </div>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => { setShowForgotPassword(false); setForgotSent(false); }}
                        >
                          Return to Login
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  // Normal Login View
                  <>
                <CardTitle className="mt-4">Login to your account</CardTitle>
                <CardDescription className="mt-2">
                  Enter your credentials to access your account
                </CardDescription>
                
                <form onSubmit={handleLogin} className="space-y-4 mt-6">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <button
                        type="button"
                        onClick={() => { setShowForgotPassword(true); setForgotEmail(loginData.email); }}
                        className="text-xs text-[#7CB342] hover:underline font-medium"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="login-password"
                        type={showLoginPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword((v) => !v)}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-700"
                        aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                        data-testid="toggle-login-password"
                      >
                        {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>

                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-500">or continue with</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    const redirectUrl = window.location.origin + '/';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="w-full py-6 border-gray-300 hover:bg-gray-50 font-medium"
                  data-testid="google-login-btn"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
                  </>
                )}
              </TabsContent>

              {/* Signup Tab */}
              <TabsContent value="signup">
                <CardTitle className="mt-4">Create an account</CardTitle>
                <CardDescription className="mt-2">
                  {signupStep === 'form'
                    ? "We'll send a 6-digit code to verify your email"
                    : `Enter the 6-digit code we sent to ${signupData.email}`}
                </CardDescription>

                {signupStep === 'form' ? (
                  <form onSubmit={handleSignup} className="space-y-4 mt-6">
                    <div>
                      <Label htmlFor="signup-name">Full Name</Label>
                      <div className="relative mt-2">
                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="John Doe"
                          value={signupData.name}
                          onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative mt-2">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="your.email@example.com"
                          value={signupData.email}
                          onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative mt-2">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="signup-password"
                          type={showSignupPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={signupData.password}
                          onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                          className="pl-10 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupPassword((v) => !v)}
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-700"
                          aria-label={showSignupPassword ? 'Hide password' : 'Show password'}
                          data-testid="toggle-signup-password"
                        >
                          {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                      data-testid="send-otp-btn"
                    >
                      {loading ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-4 mt-6">
                    <div>
                      <Label htmlFor="signup-otp">6-digit OTP</Label>
                      <Input
                        id="signup-otp"
                        inputMode="numeric"
                        maxLength={6}
                        autoComplete="one-time-code"
                        placeholder="••••••"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="text-center tracking-[10px] text-2xl py-6 mt-2"
                        data-testid="signup-otp-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Code expires in 10 minutes. Check your inbox (and spam).
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || otpCode.length !== 6}
                      className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                      data-testid="verify-otp-btn"
                    >
                      {loading ? 'Verifying...' : 'Verify & Create Account'}
                    </Button>

                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        onClick={() => { setSignupStep('form'); setOtpCode(''); }}
                        className="text-gray-600 hover:underline"
                      >
                        ← Edit details
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resendCooldown > 0}
                        className="text-[#7CB342] hover:underline disabled:text-gray-400 disabled:no-underline"
                        data-testid="resend-otp-link"
                      >
                        {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardHeader>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
